import { useState } from "react";
import { ArrowLeft, ArrowUpRight, LockKeyhole } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { useAuth } from "@/auth/AuthContext";

export default function ResetPassword() {
  const { session, checking, updatePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirmation: "" });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.password.length < 12) return setError("Use at least 12 characters for your new password.");
    if (form.password !== form.confirmation) return setError("The passwords do not match.");
    setSending(true);
    try {
      await updatePassword(form.password);
      setMessage("Your password has been updated. Redirecting to sign in…");
      await logout();
      window.setTimeout(() => navigate("/login?reset=1", { replace: true }), 700);
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="auth-page" data-testid="reset-password-page">
      <SeoHead title="Choose a new password | Ashwini Patil" description="Choose a new password for your Ashwini Patil account." canonical="https://ashwinipatil.ai/auth/reset-password" noIndex schemas={[]} />
      <div className="shell auth-shell">
        <Link to="/login" className="inline-link" data-testid="reset-password-back-link"><ArrowLeft size={15} /> Back to sign in</Link>
        <div className="auth-card">
          <div className="auth-mark"><LockKeyhole size={19} /></div>
          <div className="section-kicker">Account recovery</div>
          <h1 className="auth-title">Set a new password.</h1>
          {checking ? <p className="auth-copy" data-testid="reset-password-checking">Opening your secure recovery session…</p> : session ? (
            <form onSubmit={submit} data-testid="reset-password-form">
              <label className="form-field"><span className="form-label">New password</span><input className="form-input" type="password" minLength={12} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required data-testid="reset-password-input" /></label>
              <label className="form-field"><span className="form-label">Confirm password</span><input className="form-input" type="password" minLength={12} value={form.confirmation} onChange={(event) => setForm({ ...form, confirmation: event.target.value })} required data-testid="reset-password-confirmation" /></label>
              <button type="submit" className="button-primary" disabled={sending} data-testid="reset-password-submit">{sending ? "Updating…" : "Update password"} <ArrowUpRight size={15} /></button>
              <p className="auth-error" role="alert" data-testid="reset-password-error">{error}</p>
              <p className="auth-success" role="status" data-testid="reset-password-message">{message}</p>
            </form>
          ) : <><p className="auth-copy" data-testid="reset-password-expired">This recovery link has expired. Request a new one to continue.</p><Link to="/forgot-password" className="inline-link" data-testid="reset-password-retry-link"><ArrowLeft size={15} /> Request another link</Link></>}
        </div>
      </div>
    </main>
  );
}
